import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { SEOHead } from '../components/SEOHead';
import { getPageSEO } from '../lib/seo-config';
import { 
  Users, 
  UserPlus, 
  Mail, 
  MoreVertical, 
  Shield, 
  Edit3, 
  Trash2,
  Crown,
  User,
  BarChart3,
  Settings,
  Activity,
  TrendingUp,
  Calendar,
  AlertTriangle,
  CheckCircle,
  Building
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { TeamRoleService } from '../lib/team-roles';
import { TeamInvitationService } from '../lib/team-invitations';
import { ActivityService } from '../lib/activity';
import { TeamAnalyticsService } from '../lib/team-analytics';
import GTMService from '../lib/gtm';

type TabType = 'members' | 'analytics' | 'settings';

const TEAM_ROLES = TeamRoleService.getAvailableRoles();

interface TeamMember {
  id: string;
  email: string;
  full_name: string;
  avatar_url?: string;
  team_role: string;
  joined_at: string;
  last_active?: string;
  credits_used: number;
  permissions: string[];
}

interface TeamInvitation {
  id: string;
  email: string;
  role: string;
  status: 'pending' | 'accepted' | 'declined' | 'expired';
  invited_at?: string;
  expires_at: string;
  inviter?: {
    id: string;
    name: string;
    email: string;
  };
}

interface TeamStats {
  total_members: number;
  active_members: number;
  pending_invites: number;
  total_credits_used: number;
}

export function TeamPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('members');
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [teamInvitations, setTeamInvitations] = useState<TeamInvitation[]>([]);
  const [organizationOwnerId, setOrganizationOwnerId] = useState<string | null>(null);
  const [teamStats, setTeamStats] = useState<TeamStats>({
    total_members: 0,
    active_members: 0,
    pending_invites: 0,
    total_credits_used: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  // Form states
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [newRole, setNewRole] = useState('');
  const [sendingInvitation, setSendingInvitation] = useState(false);
  const [revokingInvitation, setRevokingInvitation] = useState<string | null>(null);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [invitationToRevoke, setInvitationToRevoke] = useState<{id: string, email: string} | null>(null);

  useEffect(() => {
    loadTeamData();
  }, [user]);

  // Prevent body scroll when modals are open
  useEffect(() => {
    if (showInviteModal || showRoleModal || showRemoveModal || showRevokeModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showInviteModal, showRoleModal, showRemoveModal, showRevokeModal]);

  const loadTeamData = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    // Note: We now handle organization creation automatically when user sends first invitation
    // So we don't block users without organizations anymore
    
    try {
      setLoading(true);
      setError(null);

      // Load team members only if user has organization
      let membersData: any[] = [];
      let invitationsData: TeamInvitation[] = [];
      
      if (user.organization_id) {
        const { members, ownerId } = await TeamRoleService.getTeamMembers(user.organization_id);
        membersData = members;
        setOrganizationOwnerId(ownerId);
        invitationsData = await TeamInvitationService.getTeamInvitations(user.organization_id, 'pending');
      } else {
        // User has no organization yet - show just themselves
        membersData = [{
          id: user.id,
          email: user.email,
          name: user.name || 'User',
          avatar: user.avatar,
          teamRole: 'admin', // They will become admin when they create org
          joinedAt: new Date().toISOString(),
          lastActive: new Date().toISOString(),
          creditsUsed: 0,
          permissions: {
            canAnalyze: true,
            canUpload: true,
            canInvite: true,
            canManageTeam: true,
            canManageProjects: true,
            canDeleteProjects: true,
            canViewReports: true,
            canExportData: true
          }
        }];
        invitationsData = [];
      }

      // Transform member data to match our interface
      const transformedMembers: TeamMember[] = membersData.map((member: any) => ({
        id: member.id,
        email: member.email,
        full_name: member.name,
        avatar_url: member.avatar,
        team_role: member.teamRole,
        joined_at: member.joinedAt,
        last_active: member.lastActive,
        credits_used: member.creditsUsed || 0,
        permissions: Object.keys(member.permissions || {})
      }));

      setTeamMembers(transformedMembers);
      
      // Calculate team stats
      const activeMembers = transformedMembers.filter((member: TeamMember) => 
        member.last_active && new Date(member.last_active) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
      ).length;
      
      const totalCreditsUsed = transformedMembers.reduce((sum: number, member: TeamMember) => sum + member.credits_used, 0);
      
      const stats = {
        total_members: transformedMembers.length,
        active_members: activeMembers,
        pending_invites: invitationsData.length,
        total_credits_used: totalCreditsUsed
      };

      setTeamStats(stats);
      setTeamInvitations(invitationsData as TeamInvitation[]);

    } catch (err) {
      console.error('Error loading team data:', err);
      setError('Failed to load team data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!user || !inviteEmail.trim() || sendingInvitation) return;

    // Store email for success message since we'll clear it immediately
    const emailToInvite = inviteEmail;
    
    // Set loading state and close modal immediately for better UX
    setSendingInvitation(true);
    setShowInviteModal(false);
    setInviteEmail('');
    setInviteRole('viewer');
    setError(null);

    try {
      // Process invitation in background - organization will be created if needed
      await TeamInvitationService.sendTeamInvitation({
        organizationId: user.organization_id || 'will-be-created', // Organization will be auto-created
        email: emailToInvite,
        role: inviteRole as 'admin' | 'editor' | 'viewer'
      });
      
      // Track team invitation
      GTMService.trackCustomEvent('invite_team_member', {
        organization_id: user.organization_id || 'new-organization',
        invited_user_email: emailToInvite,
        user_role: inviteRole,
        team_size: teamMembers.length + 1
      }, user.id);
      
      // Update success message for first-time org creation
      const successMsg = !user.organization_id 
        ? `✅ Organization created and invitation sent to ${emailToInvite}!`
        : `✅ Invitation sent successfully to ${emailToInvite}!`;
      
      setSuccessMessage(successMsg);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
      
      await loadTeamData(); // Refresh data
    } catch (err) {
      console.error('Error sending invitation:', err);
      setError('Failed to send invitation. Please try again.');
      setSuccessMessage(null);
    } finally {
      setSendingInvitation(false);
    }
  };

  const handleRevokeInvitation = async () => {
    if (!invitationToRevoke || revokingInvitation) return;
    
    setRevokingInvitation(invitationToRevoke.id);
    setError(null);
    
    try {
      await TeamInvitationService.revokeTeamInvitation(invitationToRevoke.id);
      
      // Track invitation revocation
      if (user?.organization_id) {
        GTMService.trackCustomEvent('revoke_team_invitation', {
          organization_id: user.organization_id,
          revoked_user_email: invitationToRevoke.email,
          team_size: teamMembers.length
        }, user.id);
      }
      
      setSuccessMessage(`✅ Invitation to ${invitationToRevoke.email} has been revoked successfully!`);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
      
      setShowRevokeModal(false);
      setInvitationToRevoke(null);
      await loadTeamData(); // Refresh data
    } catch (err) {
      console.error('Error revoking invitation:', err);
      setError('Failed to revoke invitation. Please try again.');
      setSuccessMessage(null);
    } finally {
      setRevokingInvitation(null);
    }
  };

  const openRevokeModal = (invitationId: string, email: string) => {
    setInvitationToRevoke({ id: invitationId, email });
    setShowRevokeModal(true);
  };

  const handleUpdateRole = async () => {
    if (!selectedMember || !newRole) return;

    try {
      const oldRole = selectedMember.team_role;
      await TeamRoleService.updateMemberRole(selectedMember.id, newRole as 'admin' | 'editor' | 'viewer');
      
      // Track role update
      if (user?.organization_id) {
        GTMService.trackCustomEvent('update_team_member_role', {
          organization_id: user.organization_id,
          member_id: selectedMember.id,
          member_email: selectedMember.email,
          old_role: oldRole,
          new_role: newRole,
          team_size: teamMembers.length
        }, user.id);
      }
      
      setShowRoleModal(false);
      setSelectedMember(null);
      setNewRole('');
      await loadTeamData(); // Refresh data
    } catch (err) {
      console.error('Error updating role:', err);
      setError('Failed to update member role. Please try again.');
    }
  };

  const handleRemoveMember = async () => {
    if (!selectedMember) return;

    try {
      await TeamRoleService.removeMemberFromTeam(selectedMember.id);
      
      // Track member removal
      if (user?.organization_id) {
        GTMService.trackCustomEvent('remove_team_member', {
          organization_id: user.organization_id,
          removed_member_id: selectedMember.id,
          removed_member_email: selectedMember.email,
          removed_member_role: selectedMember.team_role,
          team_size_after: teamMembers.length - 1
        }, user.id);
      }
      
      setShowRemoveModal(false);
      setSelectedMember(null);
      await loadTeamData(); // Refresh data
    } catch (err) {
      console.error('Error removing member:', err);
      setError('Failed to remove member. Please try again.');
    }
  };

  const isOwner = (memberId: string) => {
    return organizationOwnerId && memberId === organizationOwnerId;
  };

  const getRoleColor = (role: string, memberId: string) => {
    if (isOwner(memberId)) {
      return 'bg-amber-100 text-amber-800 border-amber-200';
    }
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'editor': return 'bg-blue-100 text-blue-800';
      case 'viewer': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRoleIcon = (role: string, memberId: string) => {
    if (isOwner(memberId)) {
      return <Crown className="w-4 h-4 text-amber-600" />;
    }
    switch (role) {
      case 'admin': return <Crown className="w-4 h-4 text-red-500" />;
      case 'editor': return <Edit3 className="w-4 h-4 text-blue-500" />;
      case 'viewer': return <Shield className="w-4 h-4 text-green-500" />;
      default: return <User className="w-4 h-4 text-gray-400" />;
    }
  };

  const getRoleLabel = (role: string, memberId: string) => {
    if (isOwner(memberId)) {
      return 'Owner';
    }
    return role;
  };

  if (loading) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading team data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <SEOHead {...getPageSEO('/team')} />
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Team Management</h1>
              <p className="text-gray-600 text-sm sm:text-base">
                {user?.organization_id 
                  ? 'Manage your team members, roles, and collaboration settings.'
                  : 'Create your team by inviting your first member. Your organization will be set up automatically.'
                }
              </p>
            </div>
            <button
              onClick={() => setShowInviteModal(true)}
              className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-blue-700 transition-all flex items-center justify-center space-x-2 min-h-[44px] w-full sm:w-auto"
            >
              <UserPlus className="w-5 h-5" />
              <span>{user?.organization_id ? 'Invite Member' : 'Start Team'}</span>
            </button>
          </div>
        </motion.div>
      </div>

      {/* First-time team setup info */}
      {!user?.organization_id && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3"
        >
          <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <Building className="w-3 h-3 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-blue-900 mb-1">Ready to build your team?</h3>
            <p className="text-sm text-blue-700">
              When you send your first invitation, we'll automatically create your organization and make you the admin. 
              You can then invite team members to collaborate on projects.
            </p>
          </div>
        </motion.div>
      )}

      {/* Error Display */}
      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2"
        >
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            ×
          </button>
        </motion.div>
      )}

      {/* Success Display */}
      {successMessage && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-2"
        >
          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
          <span className="text-green-700">{successMessage}</span>
          <button
            onClick={() => setSuccessMessage(null)}
            className="ml-auto text-green-500 hover:text-green-700"
          >
            ×
          </button>
        </motion.div>
      )}

      {/* Sending Invitation Loading */}
      {sendingInvitation && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center space-x-2"
        >
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500 flex-shrink-0"></div>
          <span className="text-blue-700">Sending invitation...</span>
        </motion.div>
      )}

      {/* Navigation Tabs */}
      <div className="mb-6 sm:mb-8">
        <div className="border-b border-gray-200">
          <div className="overflow-x-auto">
            <nav className="-mb-px flex space-x-4 sm:space-x-8 min-w-max sm:min-w-0">
              <button
                onClick={() => setActiveTab('members')}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap min-h-[44px] flex items-center ${
                  activeTab === 'members'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4" />
                  <span>Members</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap min-h-[44px] flex items-center ${
                  activeTab === 'analytics'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <BarChart3 className="w-4 h-4" />
                  <span>Analytics</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap min-h-[44px] flex items-center ${
                  activeTab === 'settings'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Settings className="w-4 h-4" />
                  <span>Settings</span>
                </div>
              </button>
            </nav>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'members' && (
        <>
      {/* Team Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{teamStats.total_members}</h3>
          <p className="text-gray-600 text-xs sm:text-sm">Total Members</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{teamStats.active_members}</h3>
          <p className="text-gray-600 text-xs sm:text-sm">Active Members</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600" />
            </div>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{teamStats.pending_invites}</h3>
          <p className="text-gray-600 text-xs sm:text-sm">Pending Invites</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{teamStats.total_credits_used}</h3>
          <p className="text-gray-600 text-xs sm:text-sm">Credits Used</p>
        </motion.div>
      </div>

      {/* Team Members Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="bg-white rounded-xl border border-gray-200 overflow-hidden"
      >
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Team Members</h2>
        </div>
        
        {/* Desktop Table View */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credits Used
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Active
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {teamMembers.map((member) => (
                <tr key={member.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      {member.avatar_url ? (
                        <img
                          src={member.avatar_url}
                          alt={member.full_name}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-500" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-gray-900">{member.full_name}</div>
                        <div className="text-sm text-gray-500">{member.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {getRoleIcon(member.team_role, member.id)}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getRoleColor(member.team_role, member.id)}`}>
                        {getRoleLabel(member.team_role, member.id)}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {member.credits_used}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {member.last_active ? 
                      new Date(member.last_active).toLocaleDateString() : 
                      'Never'
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {!isOwner(member.id) ? (
                        <>
                          <button
                            onClick={() => {
                              setSelectedMember(member);
                              setNewRole(member.team_role);
                              setShowRoleModal(true);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                            title="Change Role"
                          >
                            <Edit3 className="w-4 h-4 text-gray-400" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedMember(member);
                              setShowRemoveModal(true);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                            title="Remove Member"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </>
                      ) : (
                        <div className="flex items-center text-xs text-amber-600 bg-amber-50 px-3 py-1 rounded-lg border border-amber-200">
                          <Crown className="w-3 h-3 mr-1" />
                          Organization Owner
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="block sm:hidden divide-y divide-gray-200">
          {teamMembers.map((member) => (
            <div key={member.id} className="p-4">
              <div className="flex items-start space-x-3">
                {member.avatar_url ? (
                  <img
                    src={member.avatar_url}
                    alt={member.full_name}
                    className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <User className="w-6 h-6 text-gray-500" />
                  </div>
                )}
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-medium text-gray-900 truncate">{member.full_name}</h3>
                      <p className="text-sm text-gray-500 truncate">{member.email}</p>
                    </div>
                    
                    <div className="flex items-center space-x-1 ml-2 flex-shrink-0">
                      {!isOwner(member.id) ? (
                        <>
                          <button
                            onClick={() => {
                              setSelectedMember(member);
                              setNewRole(member.team_role);
                              setShowRoleModal(true);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                            title="Change Role"
                          >
                            <Edit3 className="w-4 h-4 text-gray-400" />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedMember(member);
                              setShowRemoveModal(true);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                            title="Remove Member"
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </button>
                        </>
                      ) : (
                        <div className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded border border-amber-200 whitespace-nowrap">
                          Owner
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      {getRoleIcon(member.team_role, member.id)}
                      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getRoleColor(member.team_role, member.id)}`}>
                        {getRoleLabel(member.team_role, member.id)}
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Credits Used:</span>
                        <span className="ml-1 font-medium text-gray-900">{member.credits_used}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Last Active:</span>
                        <span className="ml-1 text-gray-700">
                          {member.last_active ? 
                            new Date(member.last_active).toLocaleDateString() : 
                            'Never'
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </motion.div>

          {/* Pending Invitations */}
          {teamInvitations.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="mt-6 sm:mt-8 bg-white rounded-xl border border-gray-200 overflow-hidden"
            >
              <div className="p-4 sm:p-6 border-b border-gray-200">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Pending Invitations</h2>
              </div>
              
              <div className="divide-y divide-gray-200">
                {teamInvitations.map((invitation) => (
                  <div key={invitation.id} className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-gray-900 truncate">{invitation.email}</div>
                        <div className="text-sm text-gray-500 mt-1">
                          Invited as <span className="font-medium">{invitation.role}</span> • Expires {new Date(invitation.expires_at).toLocaleDateString()}
                        </div>
                      </div>
                      <button
                        onClick={() => openRevokeModal(invitation.id, invitation.email)}
                        disabled={revokingInvitation === invitation.id}
                        className="text-red-600 hover:text-red-800 text-sm font-medium bg-red-50 hover:bg-red-100 px-3 py-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-1 min-h-[44px] w-full sm:w-auto flex-shrink-0"
                      >
                        {revokingInvitation === invitation.id ? (
                          <>
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-red-600"></div>
                            <span>Revoking...</span>
                          </>
                        ) : (
                          <span>Revoke</span>
                        )}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </>
      )}

      {activeTab === 'analytics' && (
        <div className="space-y-6 sm:space-y-8">
          <div className="text-center py-8 sm:py-12 text-gray-500 px-4">
            <BarChart3 className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Team Analytics Coming Soon</h3>
            <p className="text-sm sm:text-base">Detailed team productivity and collaboration metrics will be available here.</p>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="space-y-6 sm:space-y-8">
          <div className="text-center py-8 sm:py-12 text-gray-500 px-4">
            <Settings className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Team Settings Coming Soon</h3>
            <p className="text-sm sm:text-base">Team configuration and permission settings will be available here.</p>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-md"
          >
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Invite Team Member</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base min-h-[44px]"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base min-h-[44px]"
                >
                  {TEAM_ROLES.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label} - {role.description}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                  setInviteRole('viewer');
                }}
                className="w-full sm:flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
              >
                Cancel
              </button>
              <button
                onClick={handleSendInvitation}
                disabled={!inviteEmail.trim() || sendingInvitation}
                className="w-full sm:flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 min-h-[44px]"
              >
                {sendingInvitation ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <span>Send Invite</span>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Role Change Modal */}
      {showRoleModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-md"
          >
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Change Member Role</h3>
            
            <div className="mb-4">
              <p className="text-gray-600 mb-4">
                Change role for <strong>{selectedMember.full_name}</strong>
              </p>
              
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Role
              </label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-base min-h-[44px]"
              >
                {TEAM_ROLES.map((role) => (
                  <option key={role.value} value={role.value}>
                    {role.label} - {role.description}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedMember(null);
                  setNewRole('');
                }}
                className="w-full sm:flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateRole}
                className="w-full sm:flex-1 bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all min-h-[44px]"
              >
                Update Role
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Remove Member Modal */}
      {showRemoveModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-md"
          >
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Remove Team Member</h3>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-2">
                Are you sure you want to remove <strong>{selectedMember.full_name}</strong> from the team?
              </p>
              <p className="text-sm text-red-600">
                This action cannot be undone. They will lose access to all team projects and data.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => {
                  setShowRemoveModal(false);
                  setSelectedMember(null);
                }}
                className="w-full sm:flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
              >
                Cancel
              </button>
              <button
                onClick={handleRemoveMember}
                className="w-full sm:flex-1 bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 transition-all min-h-[44px]"
              >
                Remove Member
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Revoke Invitation Modal */}
      {showRevokeModal && invitationToRevoke && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-md"
          >
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4">Revoke Invitation</h3>
            
            <div className="mb-6">
              <p className="text-gray-600 mb-2">
                Are you sure you want to revoke the invitation for <strong>{invitationToRevoke.email}</strong>?
              </p>
              <p className="text-sm text-gray-500">
                This action cannot be undone. The recipient will no longer be able to accept this invitation.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => {
                  setShowRevokeModal(false);
                  setInvitationToRevoke(null);
                }}
                disabled={revokingInvitation !== null}
                className="w-full sm:flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 min-h-[44px]"
              >
                Cancel
              </button>
              <button
                onClick={handleRevokeInvitation}
                disabled={revokingInvitation !== null}
                className="w-full sm:flex-1 bg-red-600 text-white px-4 py-3 rounded-lg hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 min-h-[44px]"
              >
                {revokingInvitation === invitationToRevoke.id ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Revoking...</span>
                  </>
                ) : (
                  <span>Revoke Invitation</span>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}